import { useState, useMemo } from "react";
import { useLocation } from "wouter";
import { trpc } from "@/utils/trpc";
import { useTranslation } from "react-i18next";
import {
  Plus,
  Ticket,
  CheckCircle2,
  XCircle,
  Inbox,
  Play,
  Pause,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  FioriFilterBar,
  FioriTable,
} from "@/components/fiori";
import type { FioriColumn } from "@/components/fiori";
import { cn } from "@/utils/utils";
import { format } from "date-fns";

// ============================================================================
// CONSTANTS
// ============================================================================

const PAGE_SIZE = 10;

function getStatusConfig(t: any) {
  return {
    new: { label: t("tickets.statuses.new"), color: "text-[#2563EB] bg-[#DBEAFE]", icon: Ticket, dotColor: "bg-[#2563EB]" },
    assigned: { label: t("tickets.statuses.assigned"), color: "text-[#7C3AED] bg-[#EDE9FE]", icon: Inbox, dotColor: "bg-[#7C3AED]" },
    inProgress: { label: t("tickets.statuses.inProgress"), color: "text-[#D97706] bg-[#FEF3C7]", icon: Play, dotColor: "bg-[#D97706]" },
    pending: { label: t("tickets.statuses.pending"), color: "text-[#6B7280] bg-[#F3F4F6]", icon: Pause, dotColor: "bg-[#6B7280]" },
    resolved: { label: t("tickets.statuses.resolved"), color: "text-[#059669] bg-[#D1FAE5]", icon: CheckCircle2, dotColor: "bg-[#059669]" },
    closed: { label: t("tickets.statuses.closed"), color: "text-[#6B7280] bg-[#F3F4F6]", icon: XCircle, dotColor: "bg-[#6B7280]" },
  };
}

function getPriorityConfig(t: any) {
  return {
    p1: { label: t("tickets.priorities.p1"), short: "P1", color: "text-[#DC2626] bg-[#FEE2E2]", dotColor: "bg-[#DC2626]" },
    p2: { label: t("tickets.priorities.p2"), short: "P2", color: "text-[#EA580C] bg-[#FFEDD5]", dotColor: "bg-[#EA580C]" },
    p3: { label: t("tickets.priorities.p3"), short: "P3", color: "text-[#D97706] bg-[#FEF3C7]", dotColor: "bg-[#D97706]" },
    p4: { label: t("tickets.priorities.p4"), short: "P4", color: "text-[#059669] bg-[#D1FAE5]", dotColor: "bg-[#059669]" },
  };
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function TicketList() {
  const { t } = useTranslation();
  
  return (
    <div className="-mx-4 md:-mx-6 -mb-4 md:-mb-6 min-h-[calc(100vh-4rem)] bg-white px-4 md:px-6 flex flex-col">
      {/* Page Header */}
      <div className="px-0 py-4 md:py-5 border-b border-[#E0E0E0]">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-lg bg-[#E8DCF5] flex items-center justify-center">
            <Ticket className="h-5 w-5 text-[#5B2C93]" />
          </div>
          <div>
            <h1 className="text-lg md:text-2xl font-semibold text-[#2C2C2C]">{t("tickets.titleLabel")}</h1>
            <p className="text-sm text-[#6B6B6B]">{t("tickets.ticketMetrics")}</p>
          </div>
        </div>
      </div>

      {/* Tickets list — dashboard, reports and configuration now live in
          Analytics and Process Configuration in the sidebar. */}
      <div className="flex-1 flex flex-col">
        <TicketsTab />
      </div>
    </div>
  );
}

// ============================================================================
// TICKETS TAB
// ============================================================================

function TicketsTab() {
  const { t } = useTranslation();
  const [, setLocation] = useLocation();
  const [subTab, setSubTab] = useState<"all" | "myQueue">("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [priorityFilter, setPriorityFilter] = useState<string>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);

  const STATUS_CONFIG = getStatusConfig(t);
  const PRIORITY_CONFIG = getPriorityConfig(t);

  const { data: allTicketsData, isLoading: allLoading } = trpc.tickets.getAll.useQuery(
    {
      status: statusFilter !== "all" ? (statusFilter as any) : undefined,
      priority: priorityFilter !== "all" ? (priorityFilter as any) : undefined,
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    },
    { enabled: subTab === "all" },
  );

  // Always fetch so the badge count is accurate on initial load
  const { data: myQueueData, isLoading: queueLoading } = trpc.tickets.getMyQueue.useQuery(undefined);

  const isLoading = subTab === "all" ? allLoading : queueLoading;
  const allTickets = allTicketsData?.tickets ?? [];
  const myQueueTickets = myQueueData ?? [];
  const ticketsList = subTab === "all" ? allTickets : myQueueTickets.slice(0, PAGE_SIZE);

  const filteredTickets = useMemo(() => {
    if (!searchQuery.trim()) return ticketsList;
    const q = searchQuery.toLowerCase();
    return ticketsList.filter(
      (t: any) =>
        t.ticketNumber?.toLowerCase().includes(q) ||
        t.title?.toLowerCase().includes(q),
    );
  }, [ticketsList, searchQuery]);

  const totalCount = allTicketsData?.total ?? 0;
  const queueCount = myQueueData?.length ?? 0;
  const totalPages = subTab === "all" ? Math.ceil(totalCount / PAGE_SIZE) : 1;
  const displayedCount = filteredTickets.length;

  // Fiori table columns
  const columns: FioriColumn<any>[] = [
    {
      key: "ticketNumber",
      header: t("tickets.ticketId"),
      width: "15%",
      render: (ticket) => (
        <span className="font-mono text-[#5B2C93] font-semibold text-sm truncate">{ticket.ticketNumber}</span>
      ),
    },
    {
      key: "title",
      header: t("tickets.problemStatement"),
      width: "30%",
      render: (ticket) => (
        <div className="min-w-0">
          <p className="font-medium text-[#2C2C2C] text-sm truncate">{ticket.title}</p>
          {ticket.description && (
            <p className="text-xs text-[#6B6B6B] truncate mt-0.5">{ticket.description}</p>
          )}
        </div>
      ),
    },
    {
      key: "status",
      header: t("tickets.status"),
      width: "12%",
      render: (ticket) => {
        const cfg = STATUS_CONFIG[ticket.status] ?? STATUS_CONFIG.new;
        return (
          <Badge variant="outline" className={cn("text-xs gap-1.5 font-medium whitespace-nowrap", cfg.color)}>
            <span className={cn("w-1.5 h-1.5 rounded-full flex-shrink-0", cfg.dotColor)} />
            {cfg.label}
          </Badge>
        );
      },
    },
    {
      key: "priority",
      header: t("tickets.priority"),
      width: "10%",
      render: (ticket) => {
        const cfg = PRIORITY_CONFIG[ticket.priority] ?? PRIORITY_CONFIG.p3;
        return (
          <Badge variant="outline" className={cn("text-xs gap-1.5 font-medium whitespace-nowrap", cfg.color)}>
            <span className={cn("w-1.5 h-1.5 rounded-full flex-shrink-0", cfg.dotColor)} />
            {cfg.short}
          </Badge>
        );
      },
    },
    {
      key: "assignee",
      header: t("tickets.assignee"),
      width: "18%",
      render: (ticket) => (
        <span className="text-sm text-[#6B6B6B] truncate block">
          {ticket.assigneeName ?? <span className="italic text-[#B0B0B0]">{t("tickets.unassigned")}</span>}
        </span>
      ),
    },
    {
      key: "createdAt",
      header: t("tickets.createdAt"),
      width: "15%",
      render: (ticket) => (
        <span className="text-sm text-[#6B6B6B] whitespace-nowrap">
          {ticket.createdAt ? format(new Date(ticket.createdAt), "MMM dd, yyyy") : "—"}
        </span>
      ),
    },
  ];

  return (
    <div className="flex-1 flex flex-col">
      {/* Sub-tabs + Filter Bar */}
      <div className="px-0 pt-4 pb-3 space-y-3">
        <div className="flex items-center gap-2 bg-[#F5F3FF] p-1 rounded-lg w-fit">
          <button
            onClick={() => { setSubTab("all"); setPage(0); }}
            className={cn(
              "px-4 py-2 text-sm font-medium rounded-md transition-all",
              subTab === "all"
                ? "bg-white text-[#5B2C93] shadow-sm ring-1 ring-[#E8DCF5]"
                : "text-[#6B6B6B] hover:text-[#5B2C93]",
            )}
          >
            {t("tickets.title")}
            <span className={cn("ml-2 text-xs px-1.5 py-0.5 rounded-full", subTab === "all" ? "bg-[#5B2C93] text-white" : "bg-[#E0E0E0] text-[#6B6B6B]")}>
              {displayedCount}
            </span>
          </button>
          <button
            onClick={() => { setSubTab("myQueue"); setPage(0); }}
            className={cn(
              "px-4 py-2 text-sm font-medium rounded-md transition-all",
              subTab === "myQueue"
                ? "bg-white text-[#5B2C93] shadow-sm ring-1 ring-[#E8DCF5]"
                : "text-[#6B6B6B] hover:text-[#5B2C93]",
            )}
          >
            {t("tickets.myQueue")}
            <span className={cn("ml-2 text-xs px-1.5 py-0.5 rounded-full", subTab === "myQueue" ? "bg-[#5B2C93] text-white" : "bg-[#E0E0E0] text-[#6B6B6B]")}>
              {Math.min(queueCount, PAGE_SIZE)}
            </span>
          </button>
        </div>

        <FioriFilterBar
          searchValue={searchQuery}
          onSearchChange={setSearchQuery}
          searchPlaceholder={t("tickets.searchPlaceholder")}
          filters={
            <div className="flex items-center gap-2">
              <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(0); }}>
                <SelectTrigger className="h-9 w-[140px] text-sm border-[#E0E0E0]">
                  <SelectValue placeholder={t("tickets.status")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("tickets.allStatuses")}</SelectItem>
                  {Object.entries(STATUS_CONFIG).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={priorityFilter} onValueChange={(v) => { setPriorityFilter(v); setPage(0); }}>
                <SelectTrigger className="h-9 w-[140px] text-sm border-[#E0E0E0]">
                  <SelectValue placeholder={t("tickets.priority")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("tickets.allPriorities")}</SelectItem>
                  {Object.entries(PRIORITY_CONFIG).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          }
          activeFilters={
            [
              statusFilter !== "all" && { key: "status", label: `${t("tickets.status")}: ${STATUS_CONFIG[statusFilter]?.label}`, onRemove: () => setStatusFilter("all") },
              priorityFilter !== "all" && { key: "priority", label: `${t("tickets.priority")}: ${PRIORITY_CONFIG[priorityFilter]?.label}`, onRemove: () => setPriorityFilter("all") },
            ].filter(Boolean) as any
          }
          trailing={
            <Button
              size="sm"
              className="bg-[#5B2C93] hover:bg-[#4A2377] text-white gap-1.5"
              onClick={() => setLocation("/tickets/new")}
            >
              <Plus className="h-4 w-4" />
              {t("tickets.newTicket")}
            </Button>
          }
        />
      </div>

      {/* Table */}
      <div className="px-0 pb-2">
        <FioriTable
          columns={columns}
          data={filteredTickets}
          isLoading={isLoading}
          emptyIcon={<Ticket className="h-8 w-8" />}
          emptyTitle={t("tickets.noTicketsFound")}
          emptyDescription={searchQuery ? t("tickets.adjustSearch") : t("tickets.createFirstTicket")}
          onRowClick={(ticket) => setLocation(`/tickets/${ticket.id}`)}
          rowKey={(ticket) => ticket.id}
          footerInfo={`${t("tickets.showing")} ${displayedCount} ${displayedCount !== 1 ? t("tickets.tickets") : t("tickets.ticket")}${subTab === "all" && totalPages > 1 ? ` (${t("tickets.page")} ${page + 1} ${t("tickets.of")} ${totalPages})` : ""}`}
        />
      </div>

      {/* Pagination */}
      {subTab === "all" && totalPages > 1 && (
        <div className="px-0 py-3 flex items-center justify-between border-t border-[#E0E0E0]">
          <span className="text-sm text-[#6B6B6B]">
            {t("tickets.page")} {page + 1} {t("tickets.of")} {totalPages}
          </span>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(Math.max(0, page - 1))}>
              {t("tickets.previous")}
            </Button>
            <Button variant="outline" size="sm" disabled={page >= totalPages - 1} onClick={() => setPage(Math.min(totalPages - 1, page + 1))}>
              {t("tickets.next")}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
